// AB3 Academy — sticky practice banner (Lane C / C.1).
//
// Поведение:
//   - Рендерится ТОЛЬКО когда useSandboxSession().isActive() === true.
//     В остальное время возвращает null — нулевое влияние на не-practice users.
//   - 3 визуальных состояния: default / warning (<5min) / expired (=0).
//   - Countdown изолирован в <CountdownChip/> (memo + own interval).
//   - «Выйти без проверки» → ConfirmModal → finish('manual') + optional DELETE.
//   - «Проверить» → CustomEvent('ab3-sandbox-verify-requested') — finish-practice
//     flow живёт в PracticeRunner (Lane B); banner не импортирует learningService.
//
// Монтируется глобально в Layout.tsx — банер должен быть виден на ЛЮБОЙ
// странице CRM, не только /academy (handoff §5.3).

import React, { useCallback, useState } from 'react';
import { Beaker } from 'lucide-react';
import { useSandboxSession } from '../../../contexts/SandboxSessionContext';
import { sandboxService } from '../../../services/sandbox.service';
import { decodeSandboxHeader } from '../../../utils/sandboxSession';
import ConfirmModal from '../../Modal/ConfirmModal';
import { showToast } from '../../../utils/toast';
import { SANDBOX_VERIFY_EVENT } from '../../../utils/sandboxEvents';
import CountdownChip from './CountdownChip';
import './PracticeSandboxBanner.css';

const WARNING_THRESHOLD_MS = 5 * 60_000;

// Re-export для backward-compat если кто-то импортировал из banner раньше.
export { SANDBOX_VERIFY_EVENT };

const PracticeSandboxBanner: React.FC = () => {
  const sandbox = useSandboxSession();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exiting, setExiting] = useState(false);
  // Локальный toggle для warning/expired стилизации — обновляется callbacks от чипа.
  const [phase, setPhase] = useState<'default' | 'warning' | 'expired'>(() => {
    const ms = sandbox.getRemainingMs();
    if (ms <= 0) return 'expired';
    if (ms < WARNING_THRESHOLD_MS) return 'warning';
    return 'default';
  });

  const handleWarning = useCallback(() => {
    setPhase((prev) => (prev === 'expired' ? 'expired' : 'warning'));
  }, []);

  const handleExpired = useCallback(() => {
    setPhase('expired');
  }, []);

  const handleVerifyClick = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(SANDBOX_VERIFY_EVENT, {
      detail: { schemaName: sandbox.schemaName },
    }));
  }, [sandbox.schemaName]);

  const handleExitClick = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleExitConfirm = useCallback(async () => {
    if (exiting) return;
    setExiting(true);

    // Достаём session_id из подписанного header'а (если есть) — для optional DELETE.
    // Backend watchdog всё равно подберёт schema по TTL, но cleanup ускоряет
    // освобождение PG-схемы. Игнорируем ошибки — это best-effort.
    const decoded = sandbox.header ? decodeSandboxHeader(sandbox.header) : null;
    const sessionId = decoded?.session_id ?? sandbox.schemaName;

    // Сначала очищаем header локально, чтобы DELETE-запрос (production-route)
    // не унёс sandbox header.
    sandbox.finish('manual');

    if (sessionId) {
      try {
        await sandboxService.deleteSession(sessionId);
      } catch {
        // best-effort; watchdog подберёт.
      }
    }

    setExiting(false);
    setConfirmOpen(false);
    showToast('Вы вышли из режима практики', 'success', 2000);
  }, [exiting, sandbox]);

  const handleExitCancel = useCallback(() => {
    if (!exiting) setConfirmOpen(false);
  }, [exiting]);

  if (!sandbox.isActive()) return null;

  const isWarning = phase === 'warning';
  const isExpired = phase === 'expired';
  const className = [
    'practice-banner',
    isWarning ? 'is-warning' : '',
    isExpired ? 'is-expired' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const ariaLive: 'off' | 'polite' | 'assertive' =
    isExpired ? 'assertive' : isWarning ? 'polite' : 'off';

  const headline = isExpired
    ? 'Время практики истекло. Начните заново.'
    : 'Режим практики. Изменения не сохраняются в боевую базу.';

  return (
    <>
      <div className={className} role="status" aria-live={ariaLive}>
        <span className="practice-banner-icon" aria-hidden>
          <Beaker size={18} />
        </span>
        <span className="practice-banner-text">{headline}</span>

        {sandbox.schemaName && (
          <span className="practice-banner-chip" title="Изолированная PG-схема">
            {sandbox.schemaName}
          </span>
        )}

        <CountdownChip
          expiresAt={sandbox.expiresAt}
          onWarning={handleWarning}
          onExpired={handleExpired}
        />

        <span className="practice-banner-actions">
          <button
            type="button"
            className="practice-banner-btn practice-banner-btn--ghost"
            onClick={handleExitClick}
            disabled={exiting}
          >
            Выйти без проверки
          </button>
          <button
            type="button"
            className="practice-banner-btn practice-banner-btn--primary"
            onClick={handleVerifyClick}
            disabled={isExpired}
          >
            {isExpired ? 'Начать заново' : 'Проверить'}
          </button>
        </span>
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={handleExitCancel}
        onConfirm={handleExitConfirm}
        title="Выйти из режима практики?"
        message="Прогресс практики не будет сохранён. Песочница закроется, изменения исчезнут."
        confirmText="Выйти"
        cancelText="Остаться"
        isDestructive
        isLoading={exiting}
        loadingText="Закрываем..."
      />
    </>
  );
};

export default PracticeSandboxBanner;
